import React from 'react';
import { Box, Typography, MenuItem, FormControl } from '@mui/material'; // Đổi TextField thành FormControl và Select
import Grid from '@mui/material/Grid'; 
import moment from 'moment';
import Select from "./ManagedDropdown";
import ManagedDropdown from "./ManagedDropdown"

/**
 * Chuyển đổi trạng thái bộ lọc ngày tháng từ UI thành điều kiện truy vấn MongoDB
 * @param {Object} appliedDateFilter - State bộ lọc (vd: { type: 'thisMonth', fromDate: '2026-03-01', toDate: '2026-03-31' })
 * @param {String} fieldName - Tên trường ngày tháng trong DB (vd: 'createdAt')
 * @returns {Object|null} - Trả về object điều kiện (vd: { createdAt: { $gte: ..., $lte: ... } }) hoặc null
 */
export const buildDateCondition = (appliedDateFilter, fieldName) => {
  // Nếu không có dữ liệu hoặc user chọn "Tất cả thời gian" thì không lọc
  if (!appliedDateFilter || !fieldName || appliedDateFilter.type === 'all') return null;

  // 1. DÒ TÌM GIÁ TRỊ TỪ NGÀY / ĐẾN NGÀY LINH ĐỘNG
  // Nhờ cơ chế mapTo, các key có thể là [fromDate, toDate] hoặc [tu_ngay, den_ngay]
  // Ta lọc bỏ key 'type' để lấy chính xác 2 giá trị ngày
  const keys = Object.keys(appliedDateFilter).filter(k => k !== 'type');
  
  const fromVal = appliedDateFilter.fromDate || appliedDateFilter[keys[0]];
  const toVal = appliedDateFilter.toDate || appliedDateFilter[keys[1]];

  const dateCond = {};

  // 2. ÉP MÚI GIỜ CHUẨN XÁC BẰNG MOMENT
  if (fromVal) {
    // startOf('day') tự động set 00:00:00 theo múi giờ Local của user, sau đó toISOString() dịch ra giờ UTC cho MongoDB
    dateCond.$gte = moment(fromVal, 'YYYY-MM-DD').startOf('day').toISOString();
  }
  
  if (toVal) {
    // endOf('day') tự động set 23:59:59.999 theo múi giờ Local
    dateCond.$lte = moment(toVal, 'YYYY-MM-DD').endOf('day').toISOString();
  }

  // 3. ĐÓNG GÓI GỬI LÊN MONGO
  if (Object.keys(dateCond).length > 0) {
    return { [fieldName]: dateCond };
  }
  
  return null;
};

/**
 * Tạo chuỗi Text hiển thị thân thiện cho người dùng dựa trên bộ lọc
 * @param {Object} appliedDateFilter 
 * @returns {String} - Chuỗi hiển thị (vd: "Tháng 5/2024")
 */
export const getDateFilterText = (appliedDateFilter) => {
  if (!appliedDateFilter) return '';
  
  if (appliedDateFilter.type === 'year') {
    return `Năm ${appliedDateFilter.year}`;
  } 
  
  if (appliedDateFilter.type === 'month') {
    return `Tháng ${appliedDateFilter.month}/${appliedDateFilter.year}`;
  } 
  
  if (appliedDateFilter.type === 'date') {
    const from = appliedDateFilter.fromDate ? new Date(appliedDateFilter.fromDate).toLocaleDateString('vi-VN') : '?';
    const to = appliedDateFilter.toDate ? new Date(appliedDateFilter.toDate).toLocaleDateString('vi-VN') : '?';
    return `Từ ${from} đến ${to}`;
  }
  
  return '';
};

export const getDateRangeByType = (type) => {
  let fromDate = '';
  let toDate = '';

  switch (type) {
    case 'today':
      fromDate = moment().format('YYYY-MM-DD');
      toDate = moment().format('YYYY-MM-DD');
      break;
    case 'thisWeek':
      fromDate = moment().startOf('week').format('YYYY-MM-DD');
      toDate = moment().endOf('week').format('YYYY-MM-DD');
      break;
    case 'lastWeek':
      fromDate = moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD');
      toDate = moment().subtract(1, 'week').endOf('week').format('YYYY-MM-DD');
      break;
    case 'thisMonth':
      fromDate = moment().startOf('month').format('YYYY-MM-DD');
      toDate = moment().endOf('month').format('YYYY-MM-DD');
      break;
    case 'lastMonth':
      fromDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
      toDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
      break;
    case 'thisYear':
      fromDate = moment().startOf('year').format('YYYY-MM-DD');
      toDate = moment().endOf('year').format('YYYY-MM-DD');
      break;
    case 'lastYear':
      fromDate = moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD');
      toDate = moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD');
      break;
    default:
      break;
  }

  return { fromDate, toDate };
};

export default function DateFilterInput({ 
  value, 
  onChange, 
  label,
  isReadOnly = false,
  mapTo = ['fromDate', 'toDate'] 
}) {
  
  // Trích xuất linh động tên key từ mapTo (VD: 'tu_ngay', 'den_ngay')
  const fromKey = mapTo[0];
  const toKey = mapTo[1];

  const safeValue = value || { type: 'all', [fromKey]: '', [toKey]: '' };
  const type = safeValue.type;
  
  // Lấy giá trị dựa trên key động
  const fromDateVal = safeValue[fromKey];
  const toDateVal = safeValue[toKey];

  // ================= XỬ LÝ CHỌN MỐC THỜI GIAN NHANH =================
  const handleTypeChange = (event) => {
    const newType = event.target.value;
    
    // NẾU LÀ CUSTOM, GIỮ NGUYÊN NGÀY CŨ CỦA NGƯỜI DÙNG
    if (newType === 'custom') {
        onChange({ type: newType, [fromKey]: fromDateVal, [toKey]: toDateVal });
        return;
    }

    // GỌI HÀM TIỆN ÍCH CHO CÁC TRƯỜNG HỢP CÒN LẠI
    const { fromDate, toDate } = getDateRangeByType(newType);
    
    onChange({ type: newType, [fromKey]: fromDate, [toKey]: toDate });
  };

  // ================= XỬ LÝ CHỌN NGÀY THỦ CÔNG =================
  const handleDateChange = (fieldKey) => (newDateValue) => {
    const dateStr = (newDateValue && moment.isMoment(newDateValue) && newDateValue.isValid()) 
      ? newDateValue.format('YYYY-MM-DD') 
      : '';
      
    onChange({ ...safeValue, type: 'custom', [fieldKey]: dateStr });
  };

  const isCustom = type === 'custom';
  const showDatePickers = type !== 'all';

  return (
    <Box>
      {label && <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', mb: 0.5, ml: 1, display: 'block' }}>{label}</Typography>}
      <Box sx={{ bgcolor: isReadOnly ? 'transparent' : 'action.hover', p: 1.5, borderRadius: '8px' }}>
        <Grid container spacing={1.5}>
          
          <Grid size={{ xs: 12, sm: 12 }}>
            {/* ========================================================= */}
            <FormControl fullWidth size="small">
              <Select
                value={type || 'all'} 
                onChange={handleTypeChange} 
                readOnly={isReadOnly}
                
                // ÉP MENU NỔI XUYÊN QUA DRAWER BẰNG SX
                /*MenuProps={{
                  sx: { zIndex: 999999 }
                }}*/
                
                sx={{ 
                  bgcolor: isReadOnly ? 'transparent' : 'background.paper', 
                  borderRadius: '8px' 
                }}
              >
                  <MenuItem value="all">Tất cả thời gian</MenuItem>
                  <MenuItem value="today">Hôm nay</MenuItem>
                  <MenuItem value="thisWeek">Tuần này</MenuItem>
                  <MenuItem value="lastWeek">Tuần trước</MenuItem>
                  <MenuItem value="thisMonth">Tháng này</MenuItem>
                  <MenuItem value="lastMonth">Tháng trước</MenuItem>
                  <MenuItem value="thisYear">Năm nay</MenuItem>
                  <MenuItem value="lastYear">Năm ngoái</MenuItem>
                  <MenuItem value="custom">Tùy chọn ngày...</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {showDatePickers && (
            <>
              <Grid size={{ xs: 6, sm: 6 }}>
                <ManagedDropdown type="datepicker" 
                  label="Từ ngày"
                  value={fromDateVal ? moment(fromDateVal, 'YYYY-MM-DD') : null}
                  onChange={handleDateChange(fromKey)} 
                  readOnly={!isCustom || isReadOnly} 
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: 'small', fullWidth: true, sx: { bgcolor: (!isCustom || isReadOnly) ? 'transparent' : 'background.paper', '& .MuiOutlinedInput-root': { borderRadius: '8px' } } } }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 6 }}>
                <ManagedDropdown type="datepicker" 
                  label="Đến ngày"
                  value={toDateVal ? moment(toDateVal, 'YYYY-MM-DD') : null}
                  onChange={handleDateChange(toKey)} 
                  readOnly={!isCustom || isReadOnly} 
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: 'small', fullWidth: true, sx: { bgcolor: (!isCustom || isReadOnly) ? 'transparent' : 'background.paper', '& .MuiOutlinedInput-root': { borderRadius: '8px' } } } }}
                />
              </Grid>
            </>
          )}

        </Grid>
      </Box>
    </Box>
  );
}